import React, { useState, useEffect, useRef } from 'react';
import { CModal, CModalHeader, CModalTitle, CModalBody, CButton } from '@coreui/react';
import { getEnv } from 'src/env';
import { getAttachmentKind, canOpenInNewTab, getExtensionLabel, KIND_ICONS } from 'src/utils/attachmentType';
import './MediaViewerModal.scss';

const MEDIA_BASE_URL = getEnv('REACT_APP_SERVER_URL');

/**
 * Спільна модалка перегляду вкладень листа на 85% екрану з гортанням.
 * Контрольований компонент — не тримає власного `open`-стану.
 * Зображення і відео рендеряться повноцінно; решта типів — плиткою з іконкою,
 * назвою і кнопками «Відкрити в новій вкладці»/«Завантажити».
 *
 * @param {boolean} open
 * @param {function} onClose
 * @param {Array<{media_id: number, name?: string, content_url: string, download_url?: string}>} attachments
 * @param {number} [startIndex]
 */
const MediaViewerModal = ({ open, onClose, attachments = [], startIndex = 0 }) => {
	const [currentIndex, setCurrentIndex] = useState(startIndex);
	const [displayMode, setDisplayMode] = useState('original');
	const [videoError, setVideoError] = useState(false);
	const thumbRefs = useRef([]);

	useEffect(() => {
		if (open) setCurrentIndex(startIndex || 0);
	}, [open, startIndex]);

	useEffect(() => {
		if (open) setDisplayMode('original');
	}, [open]);

	useEffect(() => {
		setVideoError(false);
	}, [open, currentIndex]);

	const hasMultiple = attachments.length > 1;

	const goPrev = () => setCurrentIndex((i) => (i - 1 + attachments.length) % attachments.length);
	const goNext = () => setCurrentIndex((i) => (i + 1) % attachments.length);

	useEffect(() => {
		if (!open || !hasMultiple) return;
		const handleKeyDown = (e) => {
			if (e.key === 'ArrowLeft') goPrev();
			else if (e.key === 'ArrowRight') goNext();
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [open, hasMultiple, attachments.length]);

	const safeIndex = attachments.length ? Math.min(currentIndex, attachments.length - 1) : 0;

	useEffect(() => {
		if (!open || !hasMultiple) return;
		thumbRefs.current[safeIndex]?.scrollIntoView({ block: 'nearest', inline: 'center' });
	}, [safeIndex, open, hasMultiple]);

	if (!attachments || attachments.length === 0) return null;

	const current = attachments[safeIndex];
	const currentKind = getAttachmentKind(current.name, current.content_url);
	const isVideo = currentKind === 'video';
	const isImage = currentKind === 'image';

	return (
		<CModal visible={open} onClose={onClose} className="media-viewer-modal" alignment="center">
			<CModalHeader>
				<CModalTitle>
					{current.name || 'Вкладення'}
					{hasMultiple && (
						<span className="media-viewer-modal__counter">
							{safeIndex + 1} / {attachments.length}
						</span>
					)}
					{isImage && (
						<div className="media-viewer-modal__mode-toggle">
							<button
								type="button"
								className={`media-viewer-modal__mode-btn${displayMode === 'original' ? ' media-viewer-modal__mode-btn--active' : ''}`}
								onClick={() => setDisplayMode('original')}
							>
								Оригінал
							</button>
							<button
								type="button"
								className={`media-viewer-modal__mode-btn${displayMode === 'fit' ? ' media-viewer-modal__mode-btn--active' : ''}`}
								onClick={() => setDisplayMode('fit')}
							>
								Вмістити
							</button>
						</div>
					)}
				</CModalTitle>
			</CModalHeader>
			<CModalBody className="media-viewer-modal__body">
				{hasMultiple && (
					<button
						type="button"
						className="media-viewer-modal__arrow media-viewer-modal__arrow--prev"
						onClick={goPrev}
						title="Попереднє вкладення"
					>
						‹
					</button>
				)}
				<div className={`media-viewer-modal__scroll${displayMode === 'fit' && isImage ? ' media-viewer-modal__scroll--fit' : ''}`}>
					{isVideo ? (
						videoError ? (
							<div className="media-viewer-modal__video-error">
								<p>Формат не відтворюється в браузері</p>
								<CButton
									color="secondary"
									variant="outline"
									href={MEDIA_BASE_URL + current.download_url}
									title="Завантажити файл"
								>
									Завантажити
								</CButton>
							</div>
						) : (
							<video
								key={current.media_id}
								controls
								className="media-viewer-modal__video"
								src={MEDIA_BASE_URL + current.content_url}
								onError={() => setVideoError(true)}
							/>
						)
					) : isImage ? (
						<img
							src={MEDIA_BASE_URL + current.content_url}
							alt={current.name || ''}
							className={`media-viewer-modal__image${displayMode === 'fit' ? ' media-viewer-modal__image--fit' : ''}`}
						/>
					) : (
						<div className="media-viewer-modal__file-view">
							<span className="media-viewer-modal__file-view-icon">{KIND_ICONS[currentKind]}</span>
							<div className="media-viewer-modal__file-view-name" title={current.name || ''}>
								{current.name || '—'}
							</div>
							<div className="media-viewer-modal__file-view-ext">{getExtensionLabel(current.name, current.content_url)}</div>
							<div className="media-viewer-modal__file-view-actions">
								{canOpenInNewTab(currentKind) && (
									<CButton
										color="secondary"
										variant="outline"
										onClick={() => window.open(MEDIA_BASE_URL + current.content_url, '_blank', 'noopener')}
										title="Відкрити в новій вкладці"
									>
										↗ Відкрити в новій вкладці
									</CButton>
								)}
								<CButton
									color="secondary"
									variant="outline"
									href={MEDIA_BASE_URL + current.download_url}
									title="Завантажити файл"
								>
									⬇ Завантажити
								</CButton>
							</div>
						</div>
					)}
				</div>
				{hasMultiple && (
					<button
						type="button"
						className="media-viewer-modal__arrow media-viewer-modal__arrow--next"
						onClick={goNext}
						title="Наступне вкладення"
					>
						›
					</button>
				)}
			</CModalBody>
			{hasMultiple && (
				<div className="media-viewer-modal__thumbs">
					{attachments.map((att, idx) => {
						const kind = getAttachmentKind(att.name, att.content_url);
						return (
							<div
								key={att.media_id}
								ref={(el) => (thumbRefs.current[idx] = el)}
								title={att.name || ''}
								className={`media-viewer-modal__thumb${idx === safeIndex ? ' media-viewer-modal__thumb--active' : ''}`}
								onClick={() => setCurrentIndex(idx)}
							>
								{kind === 'image' ? (
									<img src={MEDIA_BASE_URL + att.content_url} alt={att.name || ''} />
								) : kind === 'video' ? (
									<span className="media-viewer-modal__thumb-icon">{KIND_ICONS.video}</span>
								) : (
									<>
										<span className="media-viewer-modal__thumb-icon">{KIND_ICONS[kind]}</span>
										<span className="media-viewer-modal__thumb-ext">{getExtensionLabel(att.name, att.content_url)}</span>
									</>
								)}
								{kind !== 'image' && kind !== 'video' && (
									<div className="media-viewer-modal__thumb-actions">
										{canOpenInNewTab(kind) && (
											<button
												type="button"
												className="media-thumb-gallery__action-btn"
												onClick={(e) => {
													e.stopPropagation();
													window.open(MEDIA_BASE_URL + att.content_url, '_blank', 'noopener');
												}}
												title="Відкрити в новій вкладці"
											>
												↗
											</button>
										)}
										<a
											href={MEDIA_BASE_URL + att.download_url}
											className="media-thumb-gallery__action-btn"
											onClick={(e) => e.stopPropagation()}
											title="Завантажити"
										>
											⬇
										</a>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</CModal>
	);
};

export default MediaViewerModal;
